import React, { useState, useEffect } from "react";
import {
    Box,
    Button,
    CircularProgress,
    Grid,
    Typography,
    Alert,
    Divider,
    Paper,
    TextField,
    InputAdornment,
    IconButton,
    Chip,
    Stack,
} from "@mui/material";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import AssessmentIcon from "@mui/icons-material/Assessment";
import SettingsIcon from "@mui/icons-material/Settings";
import SaveIcon from "@mui/icons-material/Save";
import EditIcon from "@mui/icons-material/Edit";
import CancelIcon from "@mui/icons-material/Cancel";
import RefreshIcon from "@mui/icons-material/Refresh";
import Swal from "sweetalert2";
import { useTranslation } from "react-i18next";
import { 
    triggerRecentlyViewedCleanup, 
    getRecentlyViewedCleanupStats,
    getCleanupDaysConfig,
    updateCleanupDaysConfig 
} from "../../services/cronJobService";
import HeaderTitle from "../HeaderTitle/HeaderTitle";

const StatTile = ({ icon, label, value, color = "primary.main" }) => (
    <Paper
        variant="outlined"
        sx={{
            p: 2,
            height: "100%",
            borderRadius: 2,
            backgroundColor: "background.paper",
        }}
    >
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
            <Box
                sx={{
                    width: 36,
                    height: 36,
                    borderRadius: "10px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "action.hover",
                    color,
                }}
            >
                {icon}
            </Box>
            <Typography variant="body2" color="text.secondary">
                {label}
            </Typography>
        </Stack>
        <Typography variant="h5" fontWeight={700} color={color}>
            {value}
        </Typography>
    </Paper>
);

const RecentlyViewedCleanup = () => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [statsLoading, setStatsLoading] = useState(false);
    const [configLoading, setConfigLoading] = useState(false);
    const [stats, setStats] = useState(null);
    const [lastCleanup, setLastCleanup] = useState(null);
    const [cleanupDays, setCleanupDays] = useState(10);
    const [tempCleanupDays, setTempCleanupDays] = useState(10);
    const [isEditingDays, setIsEditingDays] = useState(false);

    // Fetch stats and config on component mount
    useEffect(() => {
        fetchCleanupConfig();
    }, []);

    // Fetch stats whenever cleanup days changes
    useEffect(() => {
        if (cleanupDays) {
            fetchStats();
        }
    }, [cleanupDays]);

    const fetchCleanupConfig = async () => {
        setConfigLoading(true);
        try {
            const response = await getCleanupDaysConfig();
            
            
            // Handle different response structures
            let days = 10; // default
            
            if (response?.data?.data?.cleanup_days !== undefined) {
                days = response.data.data.cleanup_days;
            } else if (response?.data?.cleanup_days !== undefined) {
                days = response.data.cleanup_days;
            } else if (response?.cleanup_days !== undefined) {
                days = response.cleanup_days;
            }
            
            
            setCleanupDays(days);
            setTempCleanupDays(days);
        } catch (error) {
            console.error("Error fetching cleanup config:", error);
            // Set default value on error
            setCleanupDays(10);
            setTempCleanupDays(10);
        } finally {
            setConfigLoading(false);
        }
    };

    const fetchStats = async () => {
        setStatsLoading(true);
        try {
            const response = await getRecentlyViewedCleanupStats();
            
            
            // Handle different response structures
            let statsData = null;
            
            if (response?.data?.data) {
                statsData = response.data.data;
            } else if (response?.data && typeof response.data === 'object') {
                // Check if response.data itself has the stats properties
                if (response.data.total_records !== undefined) {
                    statsData = response.data;
                }
            } else if (response?.total_records !== undefined) {
                statsData = response;
            }
            
            
            
            if (statsData) {
                setStats({
                    total_records: parseInt(statsData.total_records) || 0,
                    unique_users: parseInt(statsData.unique_users) || 0,
                    unique_documents: parseInt(statsData.unique_documents) || 0,
                    records_to_cleanup: parseInt(statsData.records_to_cleanup) || 0,
                    oldest_record: statsData.oldest_record,
                    newest_record: statsData.newest_record,
                    cleanup_days: statsData.cleanup_days || cleanupDays
                });
            }
        } catch (error) {
            console.error("Error fetching stats:", error);
            setStats(null);
        } finally {
            setStatsLoading(false);
        }
    };

    const handleSaveDaysConfig = async () => {
        // Validate input
        const daysValue = parseInt(tempCleanupDays);
        
        if (isNaN(daysValue) || daysValue < 1 || daysValue > 365) {
            Swal.fire({
                title: "Invalid Input",
                text: "Please enter a value between 1 and 365 days",
                icon: "error",
                confirmButtonText: "OK",
            });
            return;
        }

        setConfigLoading(true);
        
        try {
            const response = await updateCleanupDaysConfig(daysValue);
            
            
            // Update state optimistically
            setCleanupDays(daysValue);
            setIsEditingDays(false);
            
            Swal.fire({
                title: "Configuration Updated!",
                text: `Cleanup will now remove records older than ${daysValue} days`,
                icon: "success",
                confirmButtonText: "OK",
            });
            
            // Refresh stats with new config
            await fetchStats();
            
        } catch (error) {
            console.error("Error updating config:", error);
            
            // Verify if it actually succeeded by refetching
            setTimeout(async () => {
                await fetchCleanupConfig();
            }, 500);
            
        } finally {
            setConfigLoading(false);
        }
    };

    const handleCleanup = async () => {
        // Show confirmation dialog
        const result = await Swal.fire({
            title: "Confirm Cleanup",
            text: `This will delete all recently viewed document records older than ${cleanupDays} days. ${stats?.records_to_cleanup || 0} records will be removed.`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Yes, clean up!",
            cancelButtonText: "Cancel",
        });

        if (!result.isConfirmed) {
            return;
        }

        setLoading(true);
        try {
            const response = await triggerRecentlyViewedCleanup();
            

            // Handle different response structures
            let deletedCount = 0;
            if (response?.data?.data?.deletedCount !== undefined) {
                deletedCount = response.data.data.deletedCount;
            } else if (response?.data?.deletedCount !== undefined) {
                deletedCount = response.data.deletedCount;
            }

            Swal.fire({
                title: "Cleanup Successful!",
                text: `Successfully removed ${deletedCount} old records`,
                icon: "success",
                confirmButtonText: "OK",
            });

            setLastCleanup({
                timestamp: new Date().toLocaleString(),
                deletedCount,
            });

            // Refresh stats after cleanup
            await fetchStats();
            
        } catch (error) {
            console.error("Error during cleanup:", error);
            Swal.fire({
                title: "Error",
                text: error.message || "Failed to complete cleanup",
                icon: "error",
                confirmButtonText: "OK",
            });
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        try {
            return new Date(dateString).toLocaleString();
        } catch (e) {
            return "N/A";
        }
    };

    const handleDaysChange = (e) => {
        const value = e.target.value;
        // Allow empty string or valid numbers
        if (value === "" || (!isNaN(value) && parseInt(value) >= 0)) {
            setTempCleanupDays(value === "" ? "" : parseInt(value));
        }
    };

    const handleCancelEdit = () => {
        setTempCleanupDays(cleanupDays);
        setIsEditingDays(false);
    };

    return (
        <Box width="100%">
            <HeaderTitle
                title={t("UI_TEXT.RECENTLY_VIEWED_CLEANUP", { defaultValue: "Recently Viewed Cleanup" })}
                subtitle={t(
                    "UI_TEXT.RECENTLY_VIEWED_CLEANUP_SUBTITLE",
                    { defaultValue: "Manage retention settings and remove stale recently viewed document records." }
                )}
                actions={
                    <Button
                        variant="roundedGreyBgSquareContained"
                        color="primary"
                        onClick={fetchStats}
                        disabled={statsLoading}
                    >
                        {statsLoading ? <CircularProgress size={18} color="inherit" /> : <RefreshIcon />}
                    </Button>
                }
            />

            <Grid container spacing={2.5}>
                <Grid item xs={12} lg={8}>
                    <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, borderRadius: 2, mb: 2.5 }}>
                        <Stack direction={{ xs: "column", md: "row" }} spacing={2} justifyContent="space-between" alignItems={{ xs: "flex-start", md: "center" }} sx={{ mb: 2 }}>
                            <Stack direction="row" spacing={1.5} alignItems="center">
                                <Box
                                    sx={{
                                        width: 42,
                                        height: 42,
                                        borderRadius: "12px",
                                        backgroundColor: "action.hover",
                                        color: "primary.main",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                    }}
                                >
                                    <SettingsIcon />
                                </Box>
                                <Box>
                                    <Typography variant="h6" fontWeight={700}>
                                        {t("UI_TEXT.CLEANUP_CONFIGURATION", { defaultValue: "Cleanup Configuration" })}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {t(
                                            "UI_TEXT.CLEANUP_CONFIGURATION_HELPER",
                                            { defaultValue: "Define how long recently viewed records are retained before they become eligible for cleanup." }
                                        )}
                                    </Typography>
                                </Box>
                            </Stack>
                            <Chip
                                color="primary"
                                variant="outlined"
                                label={t("UI_TEXT.CURRENT_THRESHOLD_DAYS", {
                                    defaultValue: "Threshold: {{days}} days",
                                    days: cleanupDays,
                                })}
                            />
                        </Stack>

                        <Alert severity="info" sx={{ mb: 2.5 }}>
                            {t(
                                "UI_TEXT.CLEANUP_CONFIGURATION_ALERT",
                                { defaultValue: "Automatic cleanup removes records older than the configured threshold. Update the value here to change future cleanup runs." }
                            )}
                        </Alert>

                        <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ xs: "stretch", md: "flex-start" }}>
                            <Box sx={{ width: { xs: "100%", md: 260 } }}>
                                <TextField
                                    label={t("UI_TEXT.CLEANUP_THRESHOLD", { defaultValue: "Cleanup Threshold" })}
                                    type="number"
                                    value={tempCleanupDays}
                                    onChange={handleDaysChange}
                                    disabled={!isEditingDays || configLoading}
                                    size="small"
                                    fullWidth
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <Typography variant="body2" color="text.secondary">
                                                    {t("GENERAL.DAYS", { defaultValue: "days" })}
                                                </Typography>
                                            </InputAdornment>
                                        ),
                                        inputProps: { min: 1, max: 365 }
                                    }}
                                    helperText={t("UI_TEXT.CLEANUP_THRESHOLD_RANGE", { defaultValue: "Range: 1-365 days" })}
                                />
                            </Box>

                            <Stack direction="row" spacing={1} flexWrap="wrap">
                                {!isEditingDays ? (
                                    <Button
                                        variant="outlined"
                                        startIcon={<EditIcon />}
                                        onClick={() => setIsEditingDays(true)}
                                        disabled={configLoading}
                                    >
                                        {t("GENERAL.EDIT", { defaultValue: "Edit" })}
                                    </Button>
                                ) : (
                                    <>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            startIcon={configLoading ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                                            onClick={handleSaveDaysConfig}
                                            disabled={
                                                configLoading ||
                                                tempCleanupDays === cleanupDays ||
                                                tempCleanupDays === "" ||
                                                tempCleanupDays < 1 ||
                                                tempCleanupDays > 365
                                            }
                                        >
                                            {configLoading
                                                ? t("GENERAL.SAVING", { defaultValue: "Saving..." })
                                                : t("GENERAL.SAVE", { defaultValue: "Save" })}
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            startIcon={<CancelIcon />}
                                            onClick={handleCancelEdit}
                                            disabled={configLoading}
                                        >
                                            {t("GENERAL.CANCEL", { defaultValue: "Cancel" })}
                                        </Button>
                                    </>
                                )}
                            </Stack>
                        </Stack>
                    </Paper>

                    <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, borderRadius: 2 }}>
                        <Stack direction={{ xs: "column", md: "row" }} spacing={2} justifyContent="space-between" alignItems={{ xs: "flex-start", md: "center" }} sx={{ mb: 2 }}>
                            <Stack direction="row" spacing={1.5} alignItems="center">
                                <Box
                                    sx={{
                                        width: 42,
                                        height: 42,
                                        borderRadius: "12px",
                                        backgroundColor: "action.hover",
                                        color: "primary.main",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                    }}
                                >
                                    <AssessmentIcon />
                                </Box>
                                <Box>
                                    <Typography variant="h6" fontWeight={700}>
                                        {t("UI_TEXT.STATISTICS", { defaultValue: "Statistics" })}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {t("UI_TEXT.RECENTLY_VIEWED_STATS_HELPER", { defaultValue: "Current record volume and cleanup exposure based on the active retention setting." })}
                                    </Typography>
                                </Box>
                            </Stack>
                            <IconButton
                                size="small"
                                onClick={fetchStats}
                                disabled={statsLoading}
                                title={t("UI_TEXT.REFRESH_STATISTICS", { defaultValue: "Refresh Statistics" })}
                            >
                                <RefreshIcon />
                            </IconButton>
                        </Stack>

                        {statsLoading ? (
                            <Box display="flex" justifyContent="center" py={6}>
                                <CircularProgress />
                            </Box>
                        ) : stats ? (
                            <>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6} xl={3}>
                                        <StatTile icon={<AssessmentIcon fontSize="small" />} label={t("UI_TEXT.TOTAL_RECORDS", { defaultValue: "Total Records" })} value={stats.total_records} />
                                    </Grid>
                                    <Grid item xs={12} sm={6} xl={3}>
                                        <StatTile icon={<AssessmentIcon fontSize="small" />} label={t("UI_TEXT.UNIQUE_USERS", { defaultValue: "Unique Users" })} value={stats.unique_users} />
                                    </Grid>
                                    <Grid item xs={12} sm={6} xl={3}>
                                        <StatTile icon={<AssessmentIcon fontSize="small" />} label={t("UI_TEXT.UNIQUE_DOCUMENTS", { defaultValue: "Unique Documents" })} value={stats.unique_documents} />
                                    </Grid>
                                    <Grid item xs={12} sm={6} xl={3}>
                                        <StatTile
                                            icon={<DeleteSweepIcon fontSize="small" />}
                                            label={t("UI_TEXT.RECORDS_TO_CLEANUP", { defaultValue: "Records to Cleanup" })}
                                            value={stats.records_to_cleanup}
                                            color={stats.records_to_cleanup > 0 ? "error.main" : "success.main"}
                                        />
                                    </Grid>
                                </Grid>

                                <Divider sx={{ my: 2.5 }} />

                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={6}>
                                        <Typography variant="body2" color="text.secondary">
                                            <strong>{t("UI_TEXT.OLDEST_RECORD", { defaultValue: "Oldest Record" })}:</strong> {formatDate(stats.oldest_record)}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <Typography variant="body2" color="text.secondary">
                                            <strong>{t("UI_TEXT.NEWEST_RECORD", { defaultValue: "Newest Record" })}:</strong> {formatDate(stats.newest_record)}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </>
                        ) : (
                            <Alert severity="info">{t("UI_TEXT.NO_STATISTICS_AVAILABLE", { defaultValue: "No statistics available" })}</Alert>
                        )}
                    </Paper>
                </Grid>

                <Grid item xs={12} lg={4}>
                    <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, borderRadius: 2, height: "100%" }}>
                        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                            <Box
                                sx={{
                                    width: 42,
                                    height: 42,
                                    borderRadius: "12px",
                                    backgroundColor: "rgba(211, 47, 47, 0.08)",
                                    color: "error.main",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                <DeleteSweepIcon />
                            </Box>
                            <Box>
                                <Typography variant="h6" fontWeight={700}>
                                    {t("UI_TEXT.CLEANUP_ACTIONS", { defaultValue: "Cleanup Actions" })}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {t("UI_TEXT.CLEANUP_ACTIONS_HELPER", { defaultValue: "Run an immediate cleanup or review the scheduled cleanup policy." })}
                                </Typography>
                            </Box>
                        </Stack>

                        <Alert severity="warning" sx={{ mb: 2 }}>
                            {t("UI_TEXT.CLEANUP_WARNING_MESSAGE", {
                                defaultValue: "Records older than {{days}} days will be permanently deleted. This action cannot be undone.",
                                days: cleanupDays,
                            })}
                        </Alert>

                        {lastCleanup && (
                            <Alert severity="success" sx={{ mb: 2 }}>
                                <Typography variant="body2">
                                    <strong>{t("UI_TEXT.LAST_CLEANUP", { defaultValue: "Last cleanup" })}:</strong> {lastCleanup.timestamp}
                                </Typography>
                                <Typography variant="body2">
                                    {t("UI_TEXT.DELETED_RECORDS_COUNT", {
                                        defaultValue: "Deleted {{count}} records",
                                        count: lastCleanup.deletedCount,
                                    })}
                                </Typography>
                            </Alert>
                        )}

                        <Button
                            variant="contained"
                            color="error"
                            size="large"
                            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <DeleteSweepIcon />}
                            onClick={handleCleanup}
                            disabled={loading || statsLoading || !stats}
                            fullWidth
                            sx={{ mb: 2 }}
                        >
                            {loading
                                ? t("UI_TEXT.CLEANING_UP", { defaultValue: "Cleaning up..." })
                                : t("UI_TEXT.RUN_CLEANUP_NOW", { defaultValue: "Run Cleanup Now" })}
                        </Button>

                        {stats?.records_to_cleanup === 0 && (
                            <Alert severity="success" icon={false} sx={{ mb: 2 }}>
                                <Typography variant="body2" align="center">
                                    {t("UI_TEXT.NO_RECORDS_NEED_CLEANUP", { defaultValue: "No records need cleanup at this time." })}
                                </Typography>
                            </Alert>
                        )}

                        <Divider sx={{ my: 2.5 }} />

                        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                            {t("UI_TEXT.AUTOMATIC_CLEANUP_SCHEDULE", { defaultValue: "Automatic Cleanup Schedule" })}
                        </Typography>
                        <Stack spacing={1}>
                            <Typography variant="body2" color="text.secondary">
                                • {t("UI_TEXT.CLEANUP_RUNS_DAILY", { defaultValue: "Runs daily at 2:00 AM" })}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                • {t("UI_TEXT.CLEANUP_DELETES_OLDER_THAN", {
                                    defaultValue: "Deletes records older than {{days}} days",
                                    days: cleanupDays,
                                })}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                • {t("UI_TEXT.CLEANUP_NO_USER_ACTION", { defaultValue: "No user action required for scheduled execution" })}
                            </Typography>
                        </Stack>
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
};

export default RecentlyViewedCleanup;
